class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.overlay = document.querySelector('.cart-notification-overlay');
    if (!this.overlay) {
      this.overlay = document.createElement('div');
      this.overlay.className = 'cart-notification-overlay';
      document.body.appendChild(this.overlay);
    }

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.notification.addEventListener('click', (evt) => {
      const removeButton = evt.target.closest('.cart-item__remove');
      if (removeButton) {
        evt.preventDefault();
        this.handleRemoveItem(removeButton);
      }
    });

    this.overlay.addEventListener('click', this.close.bind(this));
    this._scrollbarCompensation = 0;

    // Delegated change listener for quantity selections within the panel
    this.notification.addEventListener('change', (evt) => {
      const el = evt.target;
      if (!el.classList.contains('cart-item-qty-select')) return;
      this.handleQtyChange(evt);
    });

    // Delegated click listener for remove buttons
    this.notification.addEventListener('click', (evt) => {
      const removeButton = evt.target.closest('.cart-item__remove');
      if (removeButton) {
        evt.preventDefault();
        this.handleRemoveItem(removeButton);
      }
    });
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsub) this._cartUpdateUnsub();
  }

  handleQtyChange(evt) {
    const selectEl = evt.target;
    const newQty = parseInt(selectEl.value, 10);

    // Immediate visual update based on the selected option
    (function syncPriceFromOption(selectEl) {
      try {
        const selectedOption = selectEl.options[selectEl.selectedIndex];
        if (!selectedOption) return;
        const formatted = selectedOption.dataset.priceFormatted;
        if (!formatted) return;

        const lineKey = selectEl.dataset.lineKey;
        const priceEl = document.querySelector('[data-line-price-id="' + lineKey + '"]');

        if (priceEl) {
          priceEl.innerHTML = formatted;
        }
      } catch (e) {
        console.error('syncPriceFromOption error', e);
      }
    })(selectEl);

    const cartItemsEl = document.querySelector('cart-items');
    const lineIndex = selectEl.dataset.line ? parseInt(selectEl.dataset.line, 10) : undefined;
    const variantId = selectEl.dataset.quantityVariantId || undefined;

    if (cartItemsEl && typeof cartItemsEl.updateQuantity === 'function' && lineIndex) {
      cartItemsEl.updateQuantity(lineIndex, newQty, evt, document.activeElement && document.activeElement.getAttribute('name'), variantId);
      return;
    }

    this.fallbackFetchChange(selectEl, newQty);
  }

  fallbackFetchChange(selectEl, newQty) {
    const lineKey = selectEl.dataset.lineKey;
    if (!lineKey) return;

    selectEl.disabled = true;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: lineKey, quantity: newQty })
    })
      .then((res) => {
        if (!res.ok) throw new Error('Respuesta inválida');
        return res.json();
      })
      .then((cart) => {
        this.updateViewCartButton(cart);
        if (typeof cart.item_count !== 'undefined') {
          const bubbleEls = document.querySelectorAll('.cart-count-bubble, .cart-count');
          bubbleEls.forEach((node) => (node.textContent = cart.item_count));
        }
      })
      .catch((err) => {
        console.error('Error fallbackFetchChange:', err);
      })
      .finally(() => {
        selectEl.disabled = false;
      });
  }

  onExternalCartUpdate(parsedState) {
    if (!parsedState || !parsedState.items) return;

    const key = this.cartItemKey || null;
    let updatedItem;
    if (key) {
      updatedItem = parsedState.items.find((i) => i.key === key);
    }

    // Update line price if we find the item
    if (updatedItem) {
      const priceEl = this.notification.querySelector('[data-line-price-id="' + updatedItem.key + '"]');
      if (priceEl) {
        // We use the already formatted price from the response if available
        if (updatedItem.formatted_price) {
          priceEl.textContent = updatedItem.formatted_price;
        } else {
          // Fallback: calculate and format manually if necessary
          const linePrice = typeof updatedItem.final_line_price !== 'undefined'
            ? updatedItem.final_line_price
            : updatedItem.line_price || updatedItem.price * updatedItem.quantity;
          priceEl.textContent = this.formatMoney(linePrice);
        }
      }
      
      const selectEl = this.notification.querySelector(`[data-line-key="${updatedItem.key}"]`);
      if (selectEl) {
        selectEl.value = String(updatedItem.quantity);
      }
    }

    // Update bubble counter if item_count comes
    if (typeof parsedState.item_count !== 'undefined') {
      const bubbleEls = document.querySelectorAll('.cart-count-bubble, .cart-count');
      bubbleEls.forEach((node) => (node.textContent = parsedState.item_count));

      // Update subtotal shown in panel if it exists
      const subtotalEl = this.notification.querySelector('[data-cart-subtotal]');
      if (subtotalEl && typeof parsedState.items_subtotal_price !== 'undefined') {
        // Use the formatted subtotal if available
        if (parsedState.formatted_subtotal) {
          subtotalEl.textContent = parsedState.formatted_subtotal;
        } else {
          subtotalEl.textContent = this.formatMoney(parsedState.items_subtotal_price);
        }
      }
    }

    // Update the "view cart" button on the panel
    this.updateViewCartButton(parsedState);
  }

  updateViewCartButton(cart) {
    if (!cart || typeof cart.item_count === 'undefined') return;
    const btn = this.notification.querySelector('#cart-notification-button');
    if (!btn) return;

    // Maintain internal structure if you have children
    let textNode = Array.from(btn.childNodes).find((n) => n.nodeType === Node.TEXT_NODE);
    const baseText = (textNode ? textNode.textContent : btn.textContent).replace(/\(\d+\)/, '').trim();
    const newText = `${baseText} (${cart.item_count})`;

    if (textNode) {
      textNode.textContent = ` ${newText}`;
    } else {
      btn.textContent = newText;
    }

    btn.setAttribute('aria-label', newText);
  }

  // Existing methods for opening/closing/rendering etc.
  open() {
  this.notification.hidden = false;
  this.overlay.hidden = false;
  
  setTimeout(() => {
    this.overlay.classList.add('active');
    this.notification.classList.add('active', 'animate');
  }, 10);

    const comp = window.innerWidth - document.documentElement.clientWidth;
    if (comp > 0) {
      this._scrollbarCompensation = comp;
      document.body.style.paddingRight = `${comp}px`;
    }

    document.documentElement.classList.add('cart-notification-open');

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }
  
  handleRemoveItem(button) {
    const lineKey = button.dataset.lineKey;
    const productTitle = button.dataset.productTitle;
    
    if (!lineKey) return;

    // Disable the button to prevent multiple clicks
    button.disabled = true;
    button.textContent = 'Deleting...';

    // Make the request to delete the product
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    })
    .then(response => response.json())
    .then(cart => {

      // Get only the updated cart product section
      return this.fetchUpdatedCartProducts()
        .then(html => {
          // Replace only the products section, not the entire notification
          this.replaceCartProductsContent(html);
          
          // Update the item counter
          const bubbleEls = document.querySelectorAll('.cart-count-bubble, .cart-count');
          bubbleEls.forEach(node => node.textContent = cart.item_count);
          
         // Update the "View Cart" button
          this.updateViewCartButton(cart);
          
          // Show confirmation message
          this.showRemovalConfirmation(productTitle);
          
          // Post cart update event
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-notification',
              cartData: cart
            });
          }
        });
    })
    .catch(error => {
      console.error('Error removing item:', error);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = 'Delete';
        button.disabled = false;
      }, 1000);
    });
  }

  fetchUpdatedCartProducts() {
    // Make a request to get only the products section of the cart
    return fetch('/cart?section_id=cart-notification-product')
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return doc.getElementById('cart-notification-product').innerHTML;
      });
  }

  replaceCartProductsContent(html) {
    const productsContainer = this.notification.querySelector('#cart-notification-product');
    if (productsContainer) {
      productsContainer.innerHTML = html;
      
      // Re-initialize event listeners for new elements
      this.reinitializeProductEventListeners();
    }
  }

  // Method to reinitialize event listeners for products only
  reinitializeProductEventListeners() {
    // Reattach change listener for quantity selects
    const productsContainer = this.notification.querySelector('#cart-notification-product');
    if (productsContainer) {
      productsContainer.addEventListener('change', (evt) => {
        const el = evt.target;
        if (!el.classList.contains('cart-item-qty-select')) return;
        this.handleQtyChange(evt);
      });

      // Reattach click listener for remove buttons
      productsContainer.addEventListener('click', (evt) => {
        const removeButton = evt.target.closest('.cart-item__remove');
        if (removeButton) {
          evt.preventDefault();
          this.handleRemoveItem(removeButton);
        }
      });
    }
  }


  showRemovalConfirmation(productTitle) {
    // Create confirmation message element
    const confirmation = document.createElement('div');
    confirmation.className = 'cart-removal-confirmation';
    confirmation.innerHTML = `
      <p>¡${productTitle} has been removed from your cart!</p>
    `;
    
    // Style the confirmation message
    confirmation.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #f5f5f5;
      padding: 15px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      z-index: 1000;
      max-width: 300px;
    `;
    
    document.body.appendChild(confirmation);
    
    // Remove message after 3 seconds
    setTimeout(() => {
      if (document.body.contains(confirmation)) {
        document.body.removeChild(confirmation);
      }
    }, 4000);
  }
  
  close() {
  this.notification.classList.remove('active', 'animate');
  this.overlay.classList.remove('active');
  
  setTimeout(() => {
    this.notification.hidden = true;
    this.overlay.hidden = true;
  }, 300);
    document.body.removeEventListener('click', this.onBodyClick);
    document.documentElement.classList.remove('cart-notification-open');

    if (this._scrollbarCompensation) {
      document.body.style.paddingRight = '';
      this._scrollbarCompensation = 0;
    }

    if (this.activeElement) this.activeElement.focus();
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
